import { ButtonGhost, Colors, DATE_FORMATS, formatDate, Icon, Pills } from '@amelio/ui-library';

import { careerStepKindLabelKey } from './careerStepValidation';
import { GROWTH_CAPTION } from './growthStyles';
import { readPositionName } from './targetPosition';

import type { CareerStep, CareerStepKind } from '@/types/career-growth';
import type { PillsVariant } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

/** A step's kind reads as a pill, the way the timeline badges the same three kinds. */
const KIND_PILL_VARIANT: Record<CareerStepKind, PillsVariant> = {
    Past: 'default',
    Current: 'primary',
    Target: 'violet',
};

interface CareerStepRowProps {
    step: CareerStep;
    onEdit: () => void;
    onDelete: () => void;
    dataTestId: string;
}

/** One step of the editor's list: its title, kind, period and profile, then its two actions. */
export const CareerStepRow = ({ step, onEdit, onDelete, dataTestId }: CareerStepRowProps) => {
    const { t, i18n } = useAmelioTranslations();

    const monthYear = (date: string) =>
        formatDate({ date, language: i18n.language, format: DATE_FORMATS.MONTH_YEAR });

    // A closed step shows its span; the position held today runs up to "ongoing"; a target names
    // the horizon it is aimed at, which no "ongoing" applies to -- or says none is set yet.
    const readPeriod = (): string => {
        const start = step.startDate
            ? monthYear(step.startDate)
            : t('performanceProfile.growth.stepsEditor.noHorizon');

        if (step.startDate && step.endDate) {
            return `${start} – ${monthYear(step.endDate)}`;
        }
        if (step.kind === 'Current') {
            return `${start} – ${t('performanceProfile.growth.stepsEditor.ongoing')}`;
        }
        return start;
    };

    const name = readPositionName(step);
    const profileName = step.competencyProfileName
        ? getLocalizedText(step.competencyProfileName)
        : null;

    return (
        <div
            className={'flex items-start justify-between gap-3 p-3'}
            data-testid={dataTestId}
        >
            <div className={'flex min-w-0 flex-col gap-1'}>
                <div className={'flex flex-wrap items-center gap-2'}>
                    <span className={'text-navy truncate font-medium'}>{name}</span>
                    <Pills
                        variant={KIND_PILL_VARIANT[step.kind]}
                        size={'sm'}
                    >
                        {t(careerStepKindLabelKey(step.kind))}
                    </Pills>
                </div>

                <span className={GROWTH_CAPTION}>{readPeriod()}</span>

                {profileName ? (
                    <span className={'text-primary text-xs font-medium'}>
                        {`${t('performanceProfile.growth.stepsEditor.profilePrefix')} ${profileName}`}
                    </span>
                ) : null}
            </div>

            <div className={'flex flex-shrink-0 items-center gap-1'}>
                <ButtonGhost
                    size={'sm'}
                    onClick={onEdit}
                    ariaLabel={t('performanceProfile.growth.stepsEditor.editAria', {
                        title: name,
                    })}
                    leftIcon={
                        <Icon
                            name={'edit-edit-2'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-edit`}
                />
                <ButtonGhost
                    size={'sm'}
                    onClick={onDelete}
                    ariaLabel={t('performanceProfile.growth.stepsEditor.deleteAria', {
                        title: name,
                    })}
                    leftIcon={
                        <Icon
                            name={'essential-trash'}
                            size={'sm'}
                            color={Colors.ERROR}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-delete`}
                />
            </div>
        </div>
    );
};
